import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, AlertCircle, CheckCircle2, Loader2, X } from 'lucide-react';
import { aiPurchasesAPI } from '../../api';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { useToast } from '../../hooks/useToast';

const MAX_BATCH_FILES = 10;

const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
];
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp'];

const validateFile = (file) => {
  const fileExt = '.' + file.name.split('.').pop().toLowerCase();
  if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(fileExt)) {
    return { valid: false, error: `"${file.name}": only PDF and image files are allowed` };
  }
  if (file.size > 10 * 1024 * 1024) {
    return { valid: false, error: `"${file.name}": file size must be less than 10MB` };
  }
  return { valid: true };
};

const isDuplicate = (candidate, list) =>
  list.some((f) => f.name === candidate.name && f.size === candidate.size);

const PurchaseAIUpload = () => {
  const navigate = useNavigate();
  const { success, error, ToastContainer } = useToast();
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const addFiles = (incoming) => {
    const selected = Array.from(incoming);
    if (selected.length === 0) return;

    const errors = [];
    const toAdd = [];

    setFiles((prev) => {
      let next = [...prev];
      for (const file of selected) {
        if (next.length >= MAX_BATCH_FILES) {
          errors.push(`Maximum ${MAX_BATCH_FILES} files per upload`);
          break;
        }
        const validation = validateFile(file);
        if (!validation.valid) {
          errors.push(validation.error);
          continue;
        }
        if (isDuplicate(file, next)) {
          errors.push(`"${file.name}" is already in the list`);
          continue;
        }
        toAdd.push(file);
        next = [...next, file];
      }
      return toAdd.length > 0 || errors.length > 0 ? next : prev;
    });

    if (errors.length > 0) {
      setErrorMessage(errors.join('. '));
    } else {
      setErrorMessage('');
    }
    setUploadResult(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (e) => {
    addFiles(e.target.files);
  };

  const removeFile = (index) => {
    if (uploading) return;
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setErrorMessage('');
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setErrorMessage('Please select at least one file to upload');
      return;
    }

    setUploading(true);
    setErrorMessage('');
    setUploadResult(null);

    try {
      const result = await aiPurchasesAPI.uploadBatch(files);
      setUploadResult(result);

      const { summary } = result;
      if (summary.failed === 0) {
        success(
          summary.total === 1
            ? 'Receipt uploaded and processed successfully!'
            : `All ${summary.succeeded} receipts processed successfully!`
        );
      } else if (summary.succeeded > 0) {
        success(`Processed ${summary.succeeded} of ${summary.total} receipts.`);
        error(`${summary.failed} file${summary.failed === 1 ? '' : 's'} could not be processed.`);
      } else {
        error('No receipts could be processed. Check the summary below.');
      }

      setTimeout(() => {
        navigate('/tenant/purchases/ai-review');
      }, 2000);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Upload failed';
      setErrorMessage(errorMsg);
      error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading) return;
    addFiles(e.dataTransfer.files);
  };

  const handleFileInputClick = () => {
    if (!uploading) fileInputRef.current?.click();
  };

  const { summary, results } = uploadResult || {};

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-h1 font-medium text-[var(--ds-text-primary)] mb-2">
            AI Purchase Invoice Upload
          </h1>
          <p className="text-body text-[var(--ds-text-secondary)]">
            Upload purchase receipts (PDF or images) for AI-powered processing — up to{' '}
            {MAX_BATCH_FILES} files at once
          </p>
        </div>

        {uploadResult ? (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle2 className="w-6 h-6 text-green-500" />
                <h2 className="text-h2 font-medium text-[var(--ds-text-primary)]">
                  Batch Upload Complete
                </h2>
              </div>
              <p className="text-body text-[var(--ds-text-secondary)] mb-4">
                {uploadResult.message}
              </p>

              {summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  <div className="bg-[var(--ds-control-field-bg)] rounded-[var(--ds-radius-md)] p-3 text-center">
                    <p className="text-h3 font-medium text-[var(--ds-text-primary)]">
                      {summary.total}
                    </p>
                    <p className="text-meta text-[var(--ds-text-secondary)]">Total</p>
                  </div>
                  <div className="bg-[var(--ds-control-field-bg)] rounded-[var(--ds-radius-md)] p-3 text-center">
                    <p className="text-h3 font-medium text-green-600 dark:text-green-400">
                      {summary.succeeded}
                    </p>
                    <p className="text-meta text-[var(--ds-text-secondary)]">Succeeded</p>
                  </div>
                  <div className="bg-[var(--ds-control-field-bg)] rounded-[var(--ds-radius-md)] p-3 text-center">
                    <p
                      className={`text-h3 font-medium ${
                        summary.failed > 0
                          ? 'text-[var(--ds-status-danger)]'
                          : 'text-[var(--ds-text-primary)]'
                      }`}
                    >
                      {summary.failed}
                    </p>
                    <p className="text-meta text-[var(--ds-text-secondary)]">Failed</p>
                  </div>
                  <div className="bg-[var(--ds-control-field-bg)] rounded-[var(--ds-radius-md)] p-3 text-center">
                    <p className="text-h3 font-medium text-amber-600 dark:text-amber-400">
                      {summary.needsReview}
                    </p>
                    <p className="text-meta text-[var(--ds-text-secondary)]">Needs review</p>
                  </div>
                </div>
              )}

              {results?.length > 0 && (
                <ul className="space-y-2 mb-6">
                  {results.map((item) => (
                    <li
                      key={`${item.fileName}-${item.draft?.id ?? item.error}`}
                      className="flex items-start gap-3 bg-[var(--ds-control-field-bg)] rounded-[var(--ds-radius-md)] p-3"
                    >
                      {item.success ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-[var(--ds-status-danger)] shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-body font-medium text-[var(--ds-text-primary)] truncate">
                          {item.fileName}
                        </p>
                        {item.success ? (
                          <p className="text-meta text-[var(--ds-text-secondary)] capitalize">
                            {item.draft?.status?.replace(/_/g, ' ') ?? 'processed'}
                          </p>
                        ) : (
                          <p className="text-meta text-[var(--ds-status-danger)]">
                            {item.error || 'Processing failed'}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/tenant/purchases/ai-review')}
                >
                  View All Drafts
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/tenant/purchases')}
                >
                  Go to Purchases
                </Button>
              </div>
              <p className="text-meta text-[var(--ds-text-secondary)] mt-4">
                Redirecting to review page...
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-6">
              <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-[var(--ds-radius-md)] p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-1">
                      AI Experimental Feature
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      Please check and validate all extracted data carefully. AI processing is
                      experimental and may make mistakes. Always verify amounts, dates, supplier
                      information, and line items before saving.
                    </p>
                  </div>
                </div>
              </div>

              <div
                onDragOver={uploading ? undefined : handleDragOver}
                onDrop={uploading ? undefined : handleDrop}
                onClick={handleFileInputClick}
                className={`border-2 border-dashed border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] p-12 text-center transition-colors ${
                  uploading
                    ? 'border-[var(--ds-accent-primary)] opacity-75 cursor-not-allowed'
                    : 'hover:border-[var(--ds-accent-primary)] cursor-pointer'
                }`}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-12 h-12 mx-auto mb-4 text-[var(--ds-accent-primary)] animate-spin" />
                    <h3 className="text-h3 font-medium text-[var(--ds-text-primary)] mb-2">
                      Processing receipts…
                    </h3>
                    <p className="text-body text-[var(--ds-text-secondary)] mb-4">
                      The server is processing {files.length} file{files.length === 1 ? '' : 's'}{' '}
                      sequentially — please keep this page open
                    </p>
                    <div className="flex items-center justify-center gap-2 text-[var(--ds-text-secondary)]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-meta">This may take several minutes for large batches</span>
                    </div>
                  </>
                ) : (
                  <>
                    <Upload className="w-12 h-12 mx-auto mb-4 text-[var(--ds-text-secondary)]" />
                    <h3 className="text-h3 font-medium text-[var(--ds-text-primary)] mb-2">
                      Drop your receipts here
                    </h3>
                    <p className="text-body text-[var(--ds-text-secondary)] mb-4">
                      or click to browse (select multiple files)
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      id="file-upload"
                      accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={uploading}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFileInputClick();
                      }}
                      disabled={uploading || files.length >= MAX_BATCH_FILES}
                    >
                      Select Files
                    </Button>
                    <p className="text-meta text-[var(--ds-text-secondary)] mt-4">
                      PDF, JPG, PNG, GIF, WEBP — max 10MB per file, up to {MAX_BATCH_FILES} files
                    </p>
                  </>
                )}
              </div>

              {files.length > 0 && (
                <div className="mt-6 space-y-2">
                  <p className="text-meta text-[var(--ds-text-secondary)]">
                    {files.length} of {MAX_BATCH_FILES} file{files.length === 1 ? '' : 's'}{' '}
                    selected
                  </p>
                  <ul className="space-y-2">
                    {files.map((f, index) => (
                      <li
                        key={`${f.name}-${f.size}-${index}`}
                        className="bg-[var(--ds-control-field-bg)] rounded-[var(--ds-radius-md)] p-4"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-[var(--ds-text-secondary)] shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-body font-medium text-[var(--ds-text-primary)] truncate">
                              {f.name}
                            </p>
                            <p className="text-meta text-[var(--ds-text-secondary)]">
                              {(f.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(index);
                            }}
                            disabled={uploading}
                            aria-label={`Remove ${f.name}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {errorMessage && (
                <div className="mt-4 flex items-center gap-2 text-[var(--ds-status-danger)]">
                  <AlertCircle className="w-5 h-5 shrink-0" />
                  <p className="text-body">{errorMessage}</p>
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleUpload}
                  disabled={files.length === 0 || uploading}
                  className="flex-1"
                >
                  {uploading
                    ? 'Processing…'
                    : `Upload & Process${files.length > 0 ? ` (${files.length})` : ''}`}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/tenant/purchases')}
                  disabled={uploading}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <ToastContainer />
    </Layout>
  );
};

export default PurchaseAIUpload;
